/**
 * The route model: what routes exist, and how they map to and from the hash.
 *
 * Pure, with no DOM. `use-route.ts` binds it to the browser. Split for the same reason as
 * `back-target.ts`: these are the definitions worth testing, and a test should not need a window
 * object to read them.
 *
 * The hash rather than a path, for two concrete reasons:
 *   - The site is served as static files from kiddora.ir. A path route needs a host rewrite that
 *     sends unknown paths to index.html, and a missing rewrite fails as a 404 on exactly the deep
 *     link someone was sent.
 *   - The service worker caches per bundle version and deliberately never touches `/v1/`
 *     (ADR-002). A hash never reaches it as a navigation request, so routing cannot interact with
 *     the cache-version logic at all.
 *
 * Giving each game its own route buys three things beyond tidiness: the device back button leaves
 * a game instead of leaving the site, a reload during a game returns to that game rather than to
 * the menu, and "go back to the list" becomes navigation rather than component state the browser
 * knows nothing about.
 *
 *   #/                     the landing page
 *   #/about                what this is and who it is for
 *   #/enter                sign in, by phone — the same screen for a teacher and a child
 *   #/apply                a teacher asks for a class
 *   #/play                 the child's shelf
 *   #/play/<taskId>        one game
 *   #/class                the class roster
 *   #/class/all            the classes themselves: add, rename, close, choose
 *   #/class/me             the teacher's own details, and the way out
 *   #/class/games          which games this class may play
 *   #/class/s/<id>         one student
 *   #/class/goals          the objective grid, and where goals are given out
 *   #/class/compare        students against each other
 *   #/preview              the games, as a teacher sees them
 *   #/preview/<taskId>     one game, played by the teacher and recorded nowhere
 *
 * Everything past `#/enter` needs an account, and the class routes additionally need the account to
 * be a teacher's. Neither check lives here: a router that knew about authentication would have to be
 * told when a session ends, and would then hold a second copy of the truth. `isPrivate` says which
 * routes need somebody, `isTeacherOnly` says which need a teacher, and the component that owns the
 * session decides what to do about it.
 */
export type Route =
  | { kind: "landing" }
  | { kind: "about" }
  | { kind: "enter" }
  | { kind: "apply" }
  | { kind: "play" }
  | { kind: "game"; taskId: string }
  | { kind: "class" }
  | { kind: "classes" }
  | { kind: "profile" }
  | { kind: "classGames" }
  | { kind: "student"; studentId: string }
  | { kind: "goals" }
  | { kind: "compare" }
  | { kind: "preview" }
  | { kind: "previewGame"; taskId: string };

/** Routes that require somebody to be signed in. */
export const PRIVATE_ROUTES: ReadonlySet<Route["kind"]> = new Set([
  "play",
  "game",
  "class",
  "classes",
  "profile",
  "classGames",
  "student",
  "goals",
  "compare",
  "preview",
  "previewGame",
]);

/**
 * Routes a student must never reach.
 *
 * Enforced on the server too, and that is the one that matters — this is so a child who types a URL
 * lands somewhere sensible rather than on a screen that then fails four requests.
 */
export const TEACHER_ROUTES: ReadonlySet<Route["kind"]> = new Set([
  "class",
  "classes",
  "profile",
  "classGames",
  "student",
  "goals",
  "compare",
  /**
   * The preview is teacher-only, and a child reaching it would be a bug worth preventing rather
   * than a harmless detour: a game opened here records nothing, so a child who ended up in one
   * would play a full sitting that never reaches their record.
   */
  "preview",
  "previewGame",
]);

export const isPrivate = (route: Route): boolean => PRIVATE_ROUTES.has(route.kind);
export const isTeacherOnly = (route: Route): boolean => TEACHER_ROUTES.has(route.kind);

export function parse(hash: string): Route {
  const path = hash.replace(/^#\/?/, "");
  if (path === "about") return { kind: "about" };
  if (path === "enter") return { kind: "enter" };
  if (path === "apply") return { kind: "apply" };
  if (path === "play") return { kind: "play" };
  if (path === "class") return { kind: "class" };
  if (path === "class/all") return { kind: "classes" };
  if (path === "class/me") return { kind: "profile" };
  if (path === "class/games") return { kind: "classGames" };
  if (path === "class/goals") return { kind: "goals" };
  if (path === "class/compare") return { kind: "compare" };
  if (path === "preview") return { kind: "preview" };

  const preview = /^preview\/([a-z0-9-]+)$/.exec(path);
  if (preview?.[1]) return { kind: "previewGame", taskId: preview[1] };

  /**
   * A student id is a uuid, and it is pattern-checked rather than trusted.
   *
   * It comes from the URL bar. An unmatched id falls through to the landing page, which beats
   * rendering a student screen that will 403 — and the server refuses a foreign id in any case, so
   * this is about where a mistyped link LANDS, not about access.
   */
  const student = /^class\/s\/([0-9a-f-]{36})$/.exec(path);
  if (student?.[1]) return { kind: "student", studentId: student[1] };

  const game = /^play\/([a-z0-9-]+)$/.exec(path);
  // The id is pattern-checked rather than trusted: it comes from the URL bar, and an unmatched id
  // lands on the list, which is the right answer for a link to a game no longer in the pinned
  // bundle.
  if (game?.[1]) return { kind: "game", taskId: game[1] };

  return { kind: "landing" };
}

export function toHash(route: Route): string {
  switch (route.kind) {
    case "about":
      return "#/about";
    case "enter":
      return "#/enter";
    case "apply":
      return "#/apply";
    case "play":
      return "#/play";
    case "game":
      return `#/play/${route.taskId}`;
    case "class":
      return "#/class";
    case "classes":
      return "#/class/all";
    case "profile":
      return "#/class/me";
    case "classGames":
      return "#/class/games";
    case "student":
      return `#/class/s/${route.studentId}`;
    case "goals":
      return "#/class/goals";
    case "compare":
      return "#/class/compare";
    case "preview":
      return "#/preview";
    case "previewGame":
      return `#/preview/${route.taskId}`;
    default:
      return "#/";
  }
}
